import * as React from 'react';
import * as I18n from 'i18n-js';

import CoreLink from '../../components/core_link';
import { randSample } from '../../random';
import { Paths } from '../../url_helpers';
import CMSSearchInputGroup from '../../components/cms_search_input_group';
import SanitizedRender from '../../components/sanitized_render';
import { useUser } from '../../user_hooks';
import { isExperimentEnabled } from '../../user_context_helpers';
import { experiments } from '../../constants';
import RecentSearches from '../../components/recent_searches';
import { MParticleEventName } from '../../elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '../../elog/mparticle';

export const DEFAULT_URL = Paths.marketplace.expand({});
export const DEFAULT_PLACEHOLDER = I18n.t('commons.searchHeader.placeholder');

const DEFAULT_IMAGE = 'https://reverb-res.cloudinary.com/image/upload/s--ygGlXocJ--/c_scale,q_auto:good,w_1600/v1579193425/HomepageHero-1_gr0del.jpg';
const DEFAULT_SECONDARY_IMAGE = 'https://reverb-res.cloudinary.com/image/upload/s--Q2aQifT7--/c_scale,q_auto:eco,w_800/v1579193425/01022020-Homepage1_ji19vh.jpg';

const COMPONENT_NAME = 'SearchHeader';

interface IProps {
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  secondaryImageUrl?: string;
  searchPlaceholder?: string;
  actionText?: string;
  actionUrl?: string;
  renderSearchSuggestion?: boolean;
  renderSecondaryImage?: boolean;
}

export function SearchHeader(props: IProps) {
  const defaultValues = [
    I18n.t('commons.searchHeader.suggestions.fenderStratocaster'),
    I18n.t('commons.searchHeader.suggestions.elektronDigitakt'),
    I18n.t('commons.searchHeader.suggestions.fenderPrecisionBass'),
    I18n.t('commons.searchHeader.suggestions.lesPaulCustom'),
    I18n.t('commons.searchHeader.suggestions.tubeScreamer'),
    I18n.t('commons.searchHeader.suggestions.ludwigBlackBeauty'),
    I18n.t('commons.searchHeader.suggestions.ludwigJazzFestival'),
    I18n.t('commons.searchHeader.suggestions.MarshallJcm800'),
    I18n.t('commons.searchHeader.suggestions.moogModelD'),
    I18n.t('commons.searchHeader.suggestions.neumannU87'),
    I18n.t('commons.searchHeader.suggestions.procoRat'),
    I18n.t('commons.searchHeader.suggestions.rolandJuno106'),
    I18n.t('commons.searchHeader.suggestions.sureSm7b'),
    I18n.t('commons.searchHeader.suggestions.strymonTimeline'),
    I18n.t('commons.searchHeader.suggestions.taylorGsMini'),
    I18n.t('commons.searchHeader.suggestions.teenageEngineeringOp1'),
    I18n.t('commons.searchHeader.suggestions.apolloTwin'),
    I18n.t('commons.searchHeader.suggestions.voxAc30'),
    I18n.t('commons.searchHeader.suggestions.yamahaDx7'),
    I18n.t('commons.searchHeader.suggestions.zildjianNewBeat'),
  ];

  return (
    <div className="search-header">
      <div className="search-header__inner">
        <BackgroundImage {...props} />
        <div className="search-header__text">
          <h2>
            {props.subtitle || I18n.t('discovery.homepage.searchHeader.subtitle')}
          </h2>
          <h1>
            {props.title || I18n.t('discovery.homepage.searchHeader.title')}
          </h1>
          <div className="search-header__input">
            <Action {...props} />
            {props.renderSearchSuggestion &&
              <div className="mt-2">
                <Suggestion suggestions={defaultValues} />
                <RecentSearches defaultSuggestions={defaultValues} />
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

interface ISuggestionProps {
  suggestions: string[];
}

function Suggestion(props: ISuggestionProps) {
  const user = useUser();
  const suggestion = randSample(user.requestId, props.suggestions);
  if (isExperimentEnabled(user, experiments.RECENT_SEARCH_SUGGESTIONS)) { return null; }

  const clickEvent = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedSearchSuggestion,
    position: 0,
    experiments: formatExperimentsForEventAttributes(user, [
      experiments.RECENT_SEARCH_SUGGESTIONS,
    ]),
  };

  return (
    <CoreLink
      className="search-header__suggestion"
      to={Paths.marketplace.expand({ query: suggestion })}
      clickEvent={clickEvent}
    >
      <SanitizedRender
        html={I18n.t('commons.searchHeader.suggestion', {
          suggestion,
          tagOpen: '<b>',
          tagClose: '</b>',
        })}
      />
    </CoreLink>
  );
}

function BackgroundImage(props: IProps) {
  if (props.secondaryImageUrl || props.renderSecondaryImage) {
    return (
      <>
        <div
          className="search-header__bg-image search-header__bg-image--desktop"
          style={getPrimaryBackgroundImage(props)}
        />
        <div
          className="search-header__bg-image search-header__bg-image--mobile"
          style={getMobileBackgroundImage(props)}
        />
      </>
    );
  }
  return (
    <div className="search-header__bg-image"
      style={getPrimaryBackgroundImage(props)}
    />
  );
}

function getPrimaryBackgroundImage(props: IProps) {
  return { backgroundImage: `url('${props.imageUrl || DEFAULT_IMAGE}')` };
}

function getMobileBackgroundImage(props: IProps) {
  return { backgroundImage: `url('${props.secondaryImageUrl || DEFAULT_SECONDARY_IMAGE}')` };
}

function Action(props: IProps) {
  if (!props.actionText) {
    return (
      <CMSSearchInputGroup
        actionUrl={props.actionUrl || DEFAULT_URL}
        placeholder={props.searchPlaceholder || I18n.t('commons.searchHeader.placeholder')}
        largeInput
      />
    );
  }

  return (
    <CoreLink
      to={props.actionUrl || DEFAULT_URL}
      className="button button--large"
    >
      {props.actionText}
    </CoreLink>
  );
}

export default SearchHeader;



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/cms/headers/search_header.tsx